import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useCategory } from '../hooks/useCategories';
import { CommentSection } from './CommentSection';
import type { Activity } from '../types/activity';

interface ActivityCardProps {
  activity: Activity;
}

export function ActivityCard({ activity }: ActivityCardProps) {
  const navigate = useNavigate();
  const { category } = useCategory(activity.eventCategory || '');
  const [imageError, setImageError] = React.useState(false);

  const getActivityIcon = () => {
    switch (activity.type) {
      case 'event_interested':
        return '👤';
      case 'event_updated':
        return '✏️';
      case 'event_created':
        return '✨';
      default:
        return '📌';
    }
  };

  const getActivityText = () => {
    switch (activity.type) {
      case 'event_interested':
        return 'is interested in';
      case 'event_updated':
        return 'updated';
      case 'event_created':
        return 'created';
      default:
        return 'activity on';
    }
  };

  const getUpdateDetails = () => {
    if (activity.type === 'event_updated' && activity.metadata?.updateFields) {
      const fields = activity.metadata.updateFields;
      const fieldLabels: Record<string, string> = {
        title: 'Title',
        description: 'Description',
        startDate: 'Start Date',
        endDate: 'End Date',
        startTime: 'Start Time',
        endTime: 'End Time',
        location: 'Location',
        cost: 'Price',
        isFree: 'Price',
        category: 'Category',
        image: 'Image'
      };

      const updatedFieldNames = fields
        .filter(f => fieldLabels[f])
        .map(f => fieldLabels[f])
        .slice(0, 3);

      if (updatedFieldNames.length > 0) {
        return ` (${updatedFieldNames.join(', ')})`;
      }
    }
    return '';
  };

  const formatTimestamp = (timestamp: any) => {
    if (!timestamp) return '';

    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;

    return date.toLocaleDateString();
  };

  const handleClick = () => {
    navigate(`/events/${activity.eventId}`);
  };

  const handleUserClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigate(`/users/${activity.userId}`);
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow">
      {/* Header - User Info */}
      <div className="flex items-center gap-3 p-4 pb-3">
        {/* User Avatar */}
        <div
          className="flex-shrink-0 cursor-pointer"
          onClick={handleUserClick}
        >
          {activity.userAvatar ? (
            <img
              src={activity.userAvatar}
              alt={activity.userName}
              className="w-10 h-10 rounded-full object-cover hover:ring-2 hover:ring-primary-500 transition-all"
              referrerPolicy="no-referrer"
            />
          ) : (
            <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center text-primary-700 font-semibold hover:ring-2 hover:ring-primary-500 transition-all">
              {activity.userName.charAt(0).toUpperCase()}
            </div>
          )}
        </div>

        {/* User Name and Activity */}
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-1 flex-wrap">
            <span
              className="font-semibold text-gray-900 hover:text-primary-600 cursor-pointer transition-colors"
              onClick={handleUserClick}
            >
              {activity.userName}
            </span>
            <span className="text-gray-600 text-sm">{getActivityText()}</span>
            {activity.type === 'event_updated' && (
              <span className="text-gray-500 text-xs">{getUpdateDetails()}</span>
            )}
          </div>
          <div className="text-xs text-gray-500 mt-0.5">
            {formatTimestamp(activity.createdAt)}
          </div>
        </div>
      </div>

      {/* Event Image */}
      <div
        className="relative w-full bg-gray-100 cursor-pointer group"
        onClick={handleClick}
      >
        {activity.eventImage && !imageError ? (
          <img
            src={activity.eventImage}
            alt={activity.eventTitle}
            className="w-full h-64 sm:h-80 object-cover group-hover:opacity-95 transition-opacity"
            referrerPolicy="no-referrer"
            onError={() => {
              console.log('Image failed to load:', activity.eventImage);
              setImageError(true);
            }}
            loading="lazy"
          />
        ) : (
          <div className="w-full h-64 sm:h-80 flex flex-col items-center justify-center bg-gradient-to-br from-primary-50 to-primary-100">
            <svg className="w-20 h-20 text-primary-300 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <div className="text-primary-600 font-semibold text-lg">{activity.eventTitle}</div>
          </div>
        )}

        {/* Category Badge on Image */}
        {category && (
          <div
            className="absolute top-3 left-3 flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium text-white shadow-lg"
            style={{ backgroundColor: category.color || '#3b82f6' }}
          >
            {category.icon && <span>{category.icon}</span>}
            <span>{category.name}</span>
          </div>
        )}
      </div>

      {/* Event Details */}
      <div className="p-4 cursor-pointer" onClick={handleClick}>
        {/* Event Title */}
        <h3 className="font-bold text-gray-900 text-lg mb-2 line-clamp-2">
          {activity.eventTitle}
        </h3>

        {/* Event Info Grid */}
        <div className="space-y-2 text-sm text-gray-600">
          {/* Date/Time - from metadata if available */}
          {activity.metadata?.eventDate && (
            <div className="flex items-center gap-2">
              <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <span className="line-clamp-1">{activity.metadata.eventDate}</span>
            </div>
          )}

          {/* Location - from metadata if available */}
          {activity.metadata?.eventLocation && (
            <div className="flex items-center gap-2">
              <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <span className="line-clamp-1">{activity.metadata.eventLocation}</span>
            </div>
          )}

          {/* Price - from metadata if available */}
          {activity.metadata?.eventPrice !== undefined && (
            <div className="flex items-center gap-2">
              <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="font-semibold">
                {activity.metadata.eventPrice === 'free' ? (
                  <span className="text-green-600">FREE</span>
                ) : (
                  <span>${activity.metadata.eventPrice}</span>
                )}
              </span>
            </div>
          )}
        </div>

        {/* View Event Link */}
        <div className="mt-3 pt-3 border-t border-gray-100">
          <button className="text-primary-600 hover:text-primary-700 font-medium text-sm flex items-center gap-1 group">
            <span>View Event Details</span>
            <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      </div>

      {/* Comments Section */}
      <div className="px-4 pb-4 border-t border-gray-100 pt-4">
        <CommentSection
          targetType="activity"
          targetId={activity.id!}
          eventId={activity.eventId}
          eventTitle={activity.eventTitle}
          activityId={activity.id}
          activityType={activity.type}
          collapsible={true}
          maxInitialDisplay={2}
        />
      </div>
    </div>
  );
}
